import {
   AppointmentPrescriptionOnly,
   AppointmentPrescriptionOnlyQuery,
} from '@dreryk/egabinet-appointment-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import {
   HealthcareProviderLocation,
   HealthcareProviderLocationQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { PatientSimple, PatientSimpleQuery } from '@dreryk/egabinet-patient-frontend-api';
import { DoctorSimple, DoctorSimpleFromCurrentHealthcareProviderQuery } from '@dreryk/egabinet-personnel-frontend-api';
import {
   ReceptionServiceWithPractitionersSimple,
   ReceptionServiceWithPractitionersSimpleQuery,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { useCqrsGet, useCqrsSearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { commonTranslationKeys } from '@dreryk/react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';

type PrescriptionVisitRestoreModalLoaderRenderProps = {
   appointment: AppointmentPrescriptionOnly;
   patient: PatientSimple;
   practitioners: DoctorSimple[];
   healthcareProviderLocations: HealthcareProviderLocation[];
   receptionServices: ReceptionServiceWithPractitionersSimple[];
};

type PrescriptionVisitRestoreModalLoaderProps = {
   readonly appointmentId: number;
   readonly children: (renderProps: PrescriptionVisitRestoreModalLoaderRenderProps) => JSX.Element;
};

export const PrescriptionVisitRestoreModalLoader = (props: PrescriptionVisitRestoreModalLoaderProps): JSX.Element => {
   const privateMedicalServicesEnabled = protectFeature('searchingPrivateMedicalServices', true, false);

   const {
      data: appointment,
      loading: appointmentLoading,
      error: appointmentError,
   } = useCqrsGet<AppointmentPrescriptionOnly>(() => {
      const query = new AppointmentPrescriptionOnlyQuery();
      query.appointmentId = AppointmentId.of(props.appointmentId);
      return query;
   });

   const { data: patient, loading: patientLoading, error: patientError } = useCqrsGet<PatientSimple>(
      () => {
         const query = new PatientSimpleQuery();
         if (appointment) {
            query.id = Slot.of(appointment.patientId.asNumber());
         }
         return query;
      },
      {
         enabled: Boolean(appointment),
      }
   );

   const {
      data: practitioners,
      loading: practitionersLoading,
      error: practitionersError,
   } = useCqrsSearch<DoctorSimple>(() => new DoctorSimpleFromCurrentHealthcareProviderQuery());

   const {
      data: healthcareProviderLocations,
      loading: locationsLoading,
      error: locationsError,
   } = useCqrsSearch<HealthcareProviderLocation>(() => new HealthcareProviderLocationQuery());

   const {
      data: receptionServices,
      loading: receptionServicesLoading,
      error: receptionServicesError,
   } = useCqrsSearch<ReceptionServiceWithPractitionersSimple>(
      () => new ReceptionServiceWithPractitionersSimpleQuery(),
      paginations.all(),
      { enabled: privateMedicalServicesEnabled }
   );

   if (appointmentLoading || patientLoading || practitionersLoading || locationsLoading || receptionServicesLoading) {
      return <LoaderSpinner />;
   }

   if (appointmentError || patientError || practitionersError || locationsError || receptionServicesError) {
      return (
         <Message
            testId={'prescriptionVisitRestoreModalFetchError'}
            type={MessageType.NEGATIVE}
            label={commonTranslationKeys.unexpectedErrorHasOccurred}
            displayIndicator
         />
      );
   }

   return props.children({
      appointment: appointment,
      patient: patient,
      practitioners: practitioners,
      healthcareProviderLocations: healthcareProviderLocations,
      receptionServices: privateMedicalServicesEnabled ? receptionServices : [],
   });
};
